import React, { useMemo } from 'react';
import Input from './Input';
import CustomSelect from './CustomSelect';
import DatePicker from '../client/DatePicker';
import TimePicker from '../client/TimePicker';

const PAIR_GROUPS = [
  ['guest_email', 'guest_phone'],
  ['event_date', 'event_time'],
  ['event_type', 'guest_count'],
];

const MARKETING_PICKER_CLASS =
  '!h-12 !min-h-12 !rounded-[16px] !px-4 !text-base !border-[var(--m-border-light)] sm:!h-11 sm:!min-h-11 sm:!text-[0.9375rem]';

const groupFields = (fields) => {
  const used = new Set();
  const rows = [];

  for (let i = 0; i < fields.length; i += 1) {
    const field = fields[i];
    const id = field.id || `q_${i}`;
    if (used.has(id)) continue;

    const pairDef = PAIR_GROUPS.find((pair) => pair[0] === id);
    if (pairDef) {
      const mate = fields.find((f, idx) => (f.id || `q_${idx}`) === pairDef[1] && !used.has(pairDef[1]));
      if (mate) {
        used.add(id);
        used.add(mate.id);
        rows.push({ type: 'pair', fields: [field, mate] });
        continue;
      }
    }

    used.add(id);
    rows.push({ type: 'single', fields: [field] });
  }

  return rows;
};

const FieldControl = ({
  q,
  idx,
  value,
  error,
  onChange,
  size,
  disabled,
  variant,
}) => {
  const id = q.id || `q_${idx}`;
  const isMarketing = variant === 'marketing';
  const pickerSize = isMarketing ? 'lg' : size === 'lg' ? 'md' : size || 'md';

  if (q.type === 'date') {
    return (
      <div className="min-w-0 w-full">
        <DatePicker
          label={q.label}
          required={!!q.required}
          value={value || ''}
          onChange={(v) => onChange(id, v || '')}
          placeholder={q.placeholder || 'Select date'}
          allowPastDates={false}
          disabled={disabled}
          error={error}
          size={pickerSize}
          className={isMarketing ? MARKETING_PICKER_CLASS : ''}
        />
      </div>
    );
  }

  if (q.type === 'time') {
    return (
      <div className="min-w-0 w-full">
        <TimePicker
          label={q.label}
          required={!!q.required}
          value={value || ''}
          onChange={(v) => onChange(id, v || '')}
          placeholder={q.placeholder || 'Select time'}
          format="12"
          disabled={disabled}
          error={error}
          size={pickerSize}
          className={isMarketing ? MARKETING_PICKER_CLASS : ''}
        />
      </div>
    );
  }

  if (q.type === 'textarea') {
    return (
      <div className="flex min-w-0 flex-col gap-2">
        <label htmlFor={id} className={isMarketing ? 'auth-field-label' : 'text-sm font-medium text-ink-secondary'}>
          {q.label}
          {q.required ? <span className="text-red-500"> *</span> : null}
        </label>
        <textarea
          id={id}
          rows={isMarketing ? 3 : 4}
          value={value}
          disabled={disabled}
          placeholder={q.placeholder || ''}
          onChange={(e) => onChange(id, e.target.value)}
          className={
            isMarketing
              ? 'auth-textarea w-full !text-base sm:!text-[0.9375rem]'
              : 'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-ink'
          }
        />
        {error ? <p className="text-xs text-red-600">{error}</p> : null}
      </div>
    );
  }

  if (q.type === 'select') {
    const selectOptions = (q.options || [])
      .filter(Boolean)
      .map((opt) => ({ value: opt, label: opt }));
    return (
      <div className="min-w-0">
        <CustomSelect
          label={q.label + (q.required ? ' *' : '')}
          options={selectOptions}
          value={value}
          onChange={(v) => onChange(id, v)}
          placeholder={q.placeholder || 'Select…'}
          size={isMarketing ? 'md' : size === 'lg' ? 'md' : 'sm'}
          disabled={disabled}
        />
        {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
      </div>
    );
  }

  const inputType =
    q.type === 'email' ? 'email' : q.type === 'phone' ? 'tel' : q.type === 'number' ? 'number' : 'text';

  if (isMarketing) {
    return (
      <div className="flex min-w-0 flex-col gap-2">
        <label htmlFor={id} className="auth-field-label">
          {q.label}
          {q.required ? <span className="text-red-500"> *</span> : null}
        </label>
        <input
          id={id}
          type={inputType}
          inputMode={q.type === 'number' ? 'numeric' : q.type === 'phone' ? 'tel' : q.type === 'email' ? 'email' : undefined}
          autoComplete={
            q.id === 'guest_name'
              ? 'name'
              : q.type === 'email'
                ? 'email'
                : q.type === 'phone'
                  ? 'tel'
                  : undefined
          }
          value={value}
          disabled={disabled}
          placeholder={q.placeholder || ''}
          min={q.type === 'number' ? 1 : undefined}
          onChange={(e) => onChange(id, e.target.value)}
          className="auth-input !text-base sm:!text-[0.9375rem]"
        />
        {error ? <p className="text-xs text-red-600">{error}</p> : null}
      </div>
    );
  }

  return (
    <Input
      id={id}
      type={inputType}
      label={q.label}
      required={!!q.required}
      value={value}
      disabled={disabled}
      placeholder={q.placeholder || ''}
      onChange={(e) => onChange(id, e.target.value)}
      error={error}
      size={size}
      min={q.type === 'number' ? 1 : undefined}
    />
  );
};

/**
 * Renders a questionnaire schema.
 * variant="marketing" → auth-input / public lead forms
 * default → product Input / CustomSelect (vendor UI)
 */
const DynamicQuestionFields = ({
  fields = [],
  values = {},
  onChange,
  errors = {},
  size = 'md',
  disabled = false,
  variant = 'product',
}) => {
  const rows = useMemo(() => groupFields(fields), [fields]);

  if (!fields?.length) return null;

  return (
    <div className={variant === 'marketing' ? 'space-y-4 sm:space-y-5' : 'space-y-4'}>
      {rows.map((row, rowIdx) => {
        const wrapClass =
          row.type === 'pair'
            ? 'grid grid-cols-1 gap-4 min-[480px]:grid-cols-2 sm:gap-5'
            : 'min-w-0';

        return (
          <div key={`row-${rowIdx}`} className={wrapClass}>
            {row.fields.map((q, idx) => {
              const id = q.id || `q_${rowIdx}_${idx}`;
              const error = errors[id]?.[0] || errors[id] || '';
              return (
                <FieldControl
                  key={id}
                  q={q}
                  idx={idx}
                  value={values[id] ?? ''}
                  error={error}
                  onChange={onChange}
                  size={size}
                  disabled={disabled}
                  variant={variant}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default DynamicQuestionFields;
